/**
 * @module geo/components
 *
 */

import { Store } from "redux";
import { Observable } from 'rxjs/Observable'
import { fromPromise } from "rxjs/observable/fromPromise";
import { setComponentsStateCreatorP } from "../actions/creators/component.state.creator";
import { AppStore } from "../store/app";
import {
  ComponentOptions,
  IHashComponents,
  ComponentProperties
} from "../types/component";
import {
  Extension,
  ExtensionProperties,
  ExtensionType
} from "../types/extensions";
import { REGISTER_EXTENSION } from "../payload/facade";
import { registerExtensionCreatorP } from "../actions/creators/register.extension.creator";

/**
 * Base class of all components.
 */
export abstract class Component implements ComponentOptions {
  /**
   * @internalapi
   * @private
   */
  name: string;

  /**
   * @internalapi
   * @private
   */
  geoAppUid: string;

  /**
   * show or hide this component at startup
   */
  public visible: boolean = false;

  /**
   * Some components could be initialized with options
   */
  public properties: ComponentProperties = {};

  /**
   * a reference to the current store
   * @internalapi
   * @private
   */
  public store?: Store<AppStore>;

  /**
   * default type of extension available for component
   * @internalapi
   * @private
   */
  defaultExtensionType?: ExtensionType ;

  /**
   * See [[geo.Application.components]]
   * @internalapi
   * @private
   * 
   */
  constructor(geoAppUid: string, options: any) {
    Object.assign(this, options);
    this.name = this.constructor.name;
    this.geoAppUid = geoAppUid;
  }

  /**
   * @internalapi
   * @private
   */
  public connect(store: Store<AppStore>) {
    this.store = store;    
  }
  /**
   * Return current state of the component
   */
  public getState(): any {
    if (undefined === this.store) {
      throw new Error("no store defined for component");
    }
    const currentAppState = this.store.getState();
    if ( currentAppState &&
      currentAppState.components &&
      currentAppState.components[this.name] ) {
      return currentAppState.components[this.name];
    }
    return null;
  }
  /**
   * Set visibility of component
   * @since v2.1.0
   * @param state new visibility
   * @param properties optional properties for component
   */
  public setState(state: boolean, properties?: ComponentProperties ): Observable<any> {
    if (undefined === this.store) {
      throw new Error("no store defined for component");
    }
    const components: IHashComponents = {};
    components[this.name] = { visible: state, properties };
    return fromPromise(
      setComponentsStateCreatorP({ components }, this.store.dispatch)
    );
  }

  /**
   * Build an extension to add to component from properties.
   * Some components override this method to add specifics properties
   * 
   * @since v2.1.0
   */
  public buildExtension(properties: ExtensionProperties): Extension {
    if(this.defaultExtensionType === undefined){
      throw Error('No default extension type this component type.');
    }
    const type:ExtensionType = this.defaultExtensionType;
    return { type, properties } as Extension;
  }

  /**
   * Add an extension to component after init of app
   * @param properties options for components, depends of extension type
   */
  public addExtension(properties: ExtensionProperties):Observable<any>{
    if (undefined === this.store) {
      throw new Error("no store defined for component");
    }
    const extensionOptions:REGISTER_EXTENSION = this.buildExtension(properties);   
    return fromPromise(registerExtensionCreatorP(extensionOptions, this.store.dispatch));
  }
}
